"use client";

import cn from "classnames";
import { useEffect, useId, useRef, useState } from "react";
import { LeadFormIntegratedProps } from "./LeadFormIntegrated.types";
import {
  buildFormId,
  getPackageData,
  handleSubmissionFailed,
  prepareFormPackage,
  validateLeadFormProps,
} from "./leadFormUtils";
import { useFormEvents } from "./useFormEvents";

type UseLeadFormIntegratedOptions = Pick<
  LeadFormIntegratedProps,
  | "packageId"
  | "applyDomain"
  | "leadFormType"
  | "thankYouURL"
  | "isDrawerOpen"
  | "title"
  | "subtitle"
  | "location"
  | "onStepChange"
>;

interface GetMountClassNameOptions {
  styles: Record<string, string>;
  loadedClassName?: string;
  loaded: boolean;
  enableStyles?: boolean;
  enableMultiColumn?: boolean;
  leadFormType?: "TY" | "LP";
  isLastStep: boolean | 0 | null;
  extraClasses?: Record<string, boolean>;
}

export function useLeadFormIntegrated({
  packageId = "",
  applyDomain = "",
  leadFormType = "TY",
  thankYouURL,
  isDrawerOpen = false,
  title,
  subtitle,
  location,
  onStepChange,
}: UseLeadFormIntegratedOptions) {
  validateLeadFormProps({ packageId, applyDomain, leadFormType });

  const [loaded, setLoaded] = useState(false);
  const [packageLoaded, setPackageLoaded] = useState(false);
  const [formPackage, setFormPackage] = useState<any>(null);
  const [numberOfSteps, setNumberOfSteps] = useState(0);
  const [stepNumber, setStepNumber] = useState(1);
  const salt = useId();
  const formId = buildFormId(salt, packageId, applyDomain);
  const { formContainerRef, steps } = useFormEvents(formId, leadFormType, location);
  const isLastStep = steps.current && steps.total && steps.current === steps.total;

  const onStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  }, [onStepChange]);

  useEffect(() => {
    if (window.LeadFormPlugin) {
      setLoaded(true);
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    let ignore = false;

    setPackageLoaded(false);

    getPackageData(packageId, applyDomain, leadFormType)
      .then((pkg) => {
        if (!ignore) {
          setFormPackage(pkg);
          setPackageLoaded(true);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error("LeadFormIntegrated: Failed to load form package", error);
        }
      });

    return () => {
      ignore = true;
    };
  }, [leadFormType, packageId, applyDomain]);

  useEffect(() => {
    if (!loaded || !packageLoaded || !formPackage) {
      return;
    }

    if (!window.LeadFormPlugin) {
      console.error("LeadFormPlugin is not defined");
      return;
    }

    try {
      const pkg = prepareFormPackage(formPackage, thankYouURL);
      setNumberOfSteps(pkg.screens?.length || 0);

      const options = {
        package: pkg,
        templateType: "element",
        templateFrom: `${salt}-lead-form-template`,
        salt,
        thankYouURL: thankYouURL || pkg?.embedFormData?.thankYouURL || "",
        engineEventListeners: {
          sesameSubmissionFailed: (event: any) => handleSubmissionFailed(event, leadFormType, location),
          nextScreenTransitionSucceeded: () => {
            setStepNumber((prev) => {
              const next = prev + 1;
              onStepChangeRef.current?.(next);
              return next;
            });
          },
        },
      };

      const TaxiForm = new window.LeadFormPlugin(window.document, formId, options);
      TaxiForm.start();
    } catch (e) {
      console.error(e);
    }
  }, [loaded, packageLoaded, formPackage, salt, formId, thankYouURL, leadFormType, location]);

  const finalTitle = title ?? formPackage?.embedFormData?.title;
  const finalSubtitle = subtitle ?? formPackage?.embedFormData?.introText;

  const getMountClassName = ({
    styles,
    loadedClassName = "",
    loaded: isLoaded,
    enableStyles = true,
    enableMultiColumn = false,
    leadFormType: formLeadFormType = leadFormType,
    isLastStep: lastStep,
    extraClasses = {},
  }: GetMountClassNameOptions) =>
    cn("self-mount-plugin", {
      [`${loadedClassName}`]: isLoaded,
      [styles.leadForm]: enableStyles,
      [styles.multiColumn]: enableMultiColumn,
      ["taxi-last-step"]: lastStep,
      [styles.thankYouForm]: formLeadFormType === "TY",
      ...extraClasses,
    });

  return {
    formContainerRef,
    isLastStep,
    loaded,
    setLoaded,
    formId,
    salt,
    finalTitle,
    finalSubtitle,
    stepNumber,
    numberOfSteps,
    getMountClassName,
  };
}
